import { Close } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { format, setDay } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { Cafe, WorkHour } from '../../../apiTypes';
import PrimaryButton from '../../../components/PrimaryButton';
import { timeSlots } from '../../../constants/dates';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { useLocale } from '../../../contexts/locale.context';
import { updateDoc } from '../../../utils/api';
import { dateLocaleMap } from '../../../utils/dates';

type Props = {
  dayIndex: number;
  onClose: () => void;
};

const EditWorkHoursModal = ({ dayIndex, onClose }: Props) => {
  const { cafe, cafeSlug } = useAdmin();
  const { lang } = useLocale();

  const date = useMemo(() => setDay(new Date(), dayIndex), [dayIndex]);

  const [closed, setClosed] = useState(!cafe?.hours?.[dayIndex]?.[0]);
  const [withBreak, setWithBreak] = useState(!!cafe?.hours?.[dayIndex]?.[1]);

  const [firstHours, setFirstHours] = useState<WorkHour>(cafe?.hours?.[dayIndex]?.[0] || { start: '', end: '' });
  const [secondHours, setSecondHours] = useState<WorkHour>(cafe?.hours?.[dayIndex]?.[1] || { start: '', end: '' });

  const saveChanges = useCallback(() => {
    if (!cafe) {
      return;
    }

    const hours: WorkHour[] = [firstHours];

    if (withBreak && secondHours.start && secondHours.end) {
      hours.push(secondHours);
    }

    updateDoc<Cafe>(`cafes/${cafeSlug}`, {
      hours: {
        ...(cafe.hours || {}),
        [dayIndex]: closed ? null : hours,
      },
    });

    onClose();
  }, [cafe, cafeSlug, closed, dayIndex, firstHours, secondHours, withBreak, onClose]);

  return (
    <Dialog open>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {format(date, 'EEEE', { locale: dateLocaleMap[lang] })}
          <IconButton aria-label="close" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <FormControlLabel
          sx={{ width: '100%', ml: 0, mr: 0 }}
          control={<Checkbox color="secondary" checked={closed} onChange={(e) => setClosed(e.target.checked)} />}
          label={t.closed[lang]}
        />

        <FormControlLabel
          sx={{ width: '100%', ml: 0, mr: 0 }}
          control={
            <Checkbox
              color="secondary"
              checked={!closed && withBreak}
              onChange={(e) => setWithBreak(e.target.checked)}
            />
          }
          disabled={closed}
          label={t.works_with_break[lang]}
        />

        <Typography
          color={(t) => (closed ? t.palette.text.disabled : t.palette.text.primary)}
          sx={{ mt: 3, mb: 1, textAlign: 'center' }}
        >
          {t.working_hours[lang]}:
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <Select
            value={firstHours?.start || null}
            onChange={(e) => setFirstHours({ start: e.target.value || '', end: '' })}
            disabled={closed}
            sx={{ width: 100 }}
            MenuProps={{ sx: { maxHeight: 400 } }}
          >
            {timeSlots.map((time) => (
              <MenuItem key={time} value={time}>
                {time}
              </MenuItem>
            ))}
          </Select>

          <Typography color={(t) => (closed ? t.palette.text.disabled : t.palette.text.primary)}>-</Typography>

          <Select
            value={firstHours?.end}
            onChange={(e) => setFirstHours((prev) => ({ start: prev?.start || '', end: e.target.value || '' }))}
            disabled={closed}
            sx={{ width: 100 }}
            MenuProps={{ sx: { maxHeight: 400 } }}
          >
            {timeSlots.map((time) => (
              <MenuItem key={time} value={time}>
                {time}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {!closed && withBreak && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
            <Select
              value={secondHours?.start}
              onChange={(e) => setSecondHours({ start: e.target.value || '', end: '' })}
              disabled={closed}
              sx={{ width: 100 }}
              MenuProps={{ sx: { maxHeight: 400 } }}
            >
              {timeSlots.map((time) => (
                <MenuItem key={time} value={time}>
                  {time}
                </MenuItem>
              ))}
            </Select>

            <Typography color={(t) => (closed ? t.palette.text.disabled : t.palette.text.primary)}>-</Typography>

            <Select
              value={secondHours?.end}
              onChange={(e) => setSecondHours((prev) => ({ start: prev?.start || '', end: e.target.value || '' }))}
              disabled={closed}
              sx={{ width: 100 }}
              MenuProps={{ sx: { maxHeight: 400 } }}
            >
              {timeSlots.map((time) => (
                <MenuItem key={time} value={time}>
                  {time}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <PrimaryButton fullWidth type="submit" variant="contained" onClick={saveChanges}>
          {t.save_changes[lang]}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditWorkHoursModal;
